import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap, fromEvent, merge, timer, Subject } from 'rxjs';
import { Router } from '@angular/router';
import { takeUntil, throttleTime } from 'rxjs/operators';
import { PublicClientApplication, AuthenticationResult } from '@azure/msal-browser';
import { User, LoginRequest, LoginResponse, AuthState, CreateUserRequest, UpdateUserRequest } from '../models/user.model';
import { IMC_AUTH_CONFIG, IMCAuthConfig } from '../auth.config';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly IDLE_TIMEOUT: number;
  private readonly ACTIVITY_CHECK_INTERVAL = 60 * 1000;
  private readonly STORAGE_PREFIX: string;

  private authStateSubject = new BehaviorSubject<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false
  });

  public authState$ = this.authStateSubject.asObservable();
  private idleTimer: any;
  private destroy$ = new Subject<void>();

  private msalInstance: PublicClientApplication | null = null;

  private azureRedirectProcessing = new BehaviorSubject<boolean>(false);
  public azureRedirectProcessing$ = this.azureRedirectProcessing.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    @Inject(IMC_AUTH_CONFIG) private config: IMCAuthConfig
  ) {
    this.IDLE_TIMEOUT = (config.sessionTimeoutMinutes || 120) * 60 * 1000;
    this.STORAGE_PREFIX = config.storagePrefix || 'imc_auth';

    if (config.azureAD) {
      this.initializeMSAL();
    }

    this.loadAuthState();
    this.initializeIdleTimeout();
  }

  private async initializeMSAL(): Promise<void> {
    if (!this.config.azureAD) return;

    const msalConfig = {
      auth: {
        clientId: this.config.azureAD.clientId,
        authority: `https://login.microsoftonline.com/${this.config.azureAD.tenantId}`,
        redirectUri: this.config.azureAD.redirectUri || window.location.origin,
        postLogoutRedirectUri: this.config.azureAD.postLogoutRedirectUri || window.location.origin,
        navigateToLoginRequestUrl: false
      },
      cache: {
        cacheLocation: 'localStorage' as const,
        storeAuthStateInCookie: false
      }
    };

    this.msalInstance = new PublicClientApplication(msalConfig);
    await this.msalInstance.initialize();
    this.handleAzureADRedirect();
  }

  private loadAuthState(): void {
    const token = localStorage.getItem(`${this.STORAGE_PREFIX}_token`);
    const userStr = localStorage.getItem(`${this.STORAGE_PREFIX}_user`);
    const lastActivity = localStorage.getItem(`${this.STORAGE_PREFIX}_last_activity`);

    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        const now = Date.now();
        const lastActivityTime = lastActivity ? parseInt(lastActivity, 10) : now;

        if (now - lastActivityTime > this.IDLE_TIMEOUT) {
          console.log('Session expired due to inactivity');
          this.clearAuthState();
          this.router.navigate([this.config.loginRoute || '/login']);
          return;
        }

        this.authStateSubject.next({
          user,
          token,
          isAuthenticated: true
        });
        this.updateLastActivity();
      } catch (error) {
        this.clearAuthState();
      }
    }
  }

  private initializeIdleTimeout(): void {
    if (!this.isAuthenticated()) {
      return;
    }

    const events = [
      fromEvent(document, 'click'),
      fromEvent(document, 'keypress'),
      fromEvent(document, 'scroll'),
      fromEvent(document, 'mousemove'),
      fromEvent(document, 'touchstart')
    ];

    merge(...events)
      .pipe(
        throttleTime(30000),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.isAuthenticated()) {
          this.updateLastActivity();
          this.resetIdleTimer();
        }
      });

    this.resetIdleTimer();

    timer(this.ACTIVITY_CHECK_INTERVAL, this.ACTIVITY_CHECK_INTERVAL)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.checkIdleTimeout();
      });
  }

  private updateLastActivity(): void {
    localStorage.setItem(`${this.STORAGE_PREFIX}_last_activity`, Date.now().toString());
  }

  private resetIdleTimer(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
    }

    this.idleTimer = setTimeout(() => {
      if (this.isAuthenticated()) {
        console.log('Session expired due to inactivity');
        this.logout();
        this.router.navigate([this.config.loginRoute || '/login'], {
          queryParams: { expired: 'true' }
        });
      }
    }, this.IDLE_TIMEOUT);
  }

  private checkIdleTimeout(): void {
    const lastActivity = localStorage.getItem(`${this.STORAGE_PREFIX}_last_activity`);
    if (lastActivity && this.isAuthenticated()) {
      const now = Date.now();
      const lastActivityTime = parseInt(lastActivity, 10);

      if (now - lastActivityTime > this.IDLE_TIMEOUT) {
        console.log('Session expired due to inactivity check');
        this.logout();
        this.router.navigate([this.config.loginRoute || '/login'], {
          queryParams: { expired: 'true' }
        });
      }
    }
  }

  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.config.apiUrl}/auth/login`, credentials)
      .pipe(
        tap(response => {
          this.setAuthState(response.user, response.token);
          this.initializeIdleTimeout();
        })
      );
  }

  logout(): void {
    this.clearAuthState();
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
    }
    this.router.navigate([this.config.loginRoute || '/']);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
    }
  }

  getUsers(): Observable<User[]> {
    return this.http.get<User[]>(`${this.config.apiUrl}/auth/users`);
  }

  createUser(userData: CreateUserRequest): Observable<User> {
    return this.http.post<User>(`${this.config.apiUrl}/auth/users`, userData);
  }

  updateUser(userId: string, userData: UpdateUserRequest): Observable<User> {
    return this.http.put<User>(`${this.config.apiUrl}/auth/users/${userId}`, userData);
  }

  deleteUser(userId: string): Observable<void> {
    return this.http.delete<void>(`${this.config.apiUrl}/auth/users/${userId}`);
  }

  private setAuthState(user: User, token: string): void {
    localStorage.setItem(`${this.STORAGE_PREFIX}_token`, token);
    localStorage.setItem(`${this.STORAGE_PREFIX}_user`, JSON.stringify(user));
    this.updateLastActivity();

    this.authStateSubject.next({
      user,
      token,
      isAuthenticated: true
    });
  }

  private clearAuthState(): void {
    localStorage.removeItem(`${this.STORAGE_PREFIX}_token`);
    localStorage.removeItem(`${this.STORAGE_PREFIX}_user`);
    localStorage.removeItem(`${this.STORAGE_PREFIX}_last_activity`);

    this.authStateSubject.next({
      user: null,
      token: null,
      isAuthenticated: false
    });
  }

  getToken(): string | null {
    return localStorage.getItem(`${this.STORAGE_PREFIX}_token`);
  }

  isAuthenticated(): boolean {
    return this.authStateSubject.value.isAuthenticated;
  }

  getCurrentUser(): User | null {
    return this.authStateSubject.value.user;
  }

  isAdmin(): boolean {
    const user = this.getCurrentUser();
    return user?.isAdmin || false;
  }

  hasGroup(groupName: string): boolean {
    const user = this.getCurrentUser();
    if (!user?.groups) return false;
    return user.groups.some(g => g.name === groupName);
  }

  isInAdministratorsGroup(): boolean {
    return this.hasGroup('Administrators');
  }

  canAccessSearch(): boolean {
    return this.isAdmin() || this.isInAdministratorsGroup() || this.hasGroup('Search');
  }

  canAccessWatchRules(): boolean {
    return this.isAdmin() || this.isInAdministratorsGroup() || this.hasGroup('WatchRules');
  }

  canAccessSettings(): boolean {
    return this.isAdmin() || this.isInAdministratorsGroup() || this.hasGroup('Settings');
  }

  canAccessUsers(): boolean {
    return this.isAdmin() || this.isInAdministratorsGroup();
  }

  canAccessTelemetry(): boolean {
    return this.isAdmin() || this.isInAdministratorsGroup() || this.hasGroup('Telemetry');
  }

  canViewSentences(): boolean {
    return this.isAdmin() || this.isInAdministratorsGroup() || this.hasGroup('SentencesViewer');
  }

  hasAnyPermission(): boolean {
    return this.canAccessSearch() || this.canAccessWatchRules() || this.canAccessSettings() || this.canAccessUsers() || this.canAccessTelemetry();
  }

  loginWithAzureAD(): Observable<LoginResponse> {
    if (!this.msalInstance) {
      console.error('[Azure AD] MSAL not initialized');
      return new Observable<LoginResponse>();
    }

    console.log('[Azure AD] Initiating redirect login');
    const loginRequest = {
      scopes: ['openid', 'profile', 'email', 'User.Read'],
      extraQueryParameters: {
        claims: JSON.stringify({
          id_token: {
            groups: null
          }
        })
      }
    };

    this.msalInstance.loginRedirect(loginRequest);
    return new Observable<LoginResponse>();
  }

  private handleAzureADRedirect(): void {
    if (!this.msalInstance) return;

    console.log('[Azure AD] Checking for redirect response...');
    this.msalInstance.handleRedirectPromise()
      .then((authResult: AuthenticationResult | null) => {
        if (authResult) {
          console.log('[Azure AD] Redirect authentication successful, exchanging token with backend');
          this.azureRedirectProcessing.next(true);

          // Call backend to exchange Azure token for app JWT and create/update user in DynamoDB
          this.http.post<LoginResponse>(`${this.config.apiUrl}/auth/login`, {
            azureToken: authResult.idToken
          }).subscribe({
            next: (response) => {
              console.log('[Azure AD] Backend authentication successful', { userId: response.user.id, email: response.user.email });
              this.setAuthState(response.user, response.token);
              this.initializeIdleTimeout();
              this.azureRedirectProcessing.next(false);
            },
            error: (error) => {
              console.error('[Azure AD] Backend authentication failed:', error);
              this.azureRedirectProcessing.next(false);
              this.router.navigate([this.config.loginRoute || '/login'], {
                queryParams: { error: 'backend_auth_error' }
              });
            }
          });
        } else {
          console.log('[Azure AD] No redirect response found');
        }
      })
      .catch((error) => {
        console.error('[Azure AD] Redirect handling failed:', error);
        this.azureRedirectProcessing.next(false);
        this.router.navigate([this.config.loginRoute || '/login'], {
          queryParams: { error: 'azure_ad_error' }
        });
      });
  }

  logoutAzureAD(): void {
    if (!this.msalInstance) {
      this.logout();
      return;
    }

    const logoutRequest = {
      postLogoutRedirectUri: this.config.azureAD?.postLogoutRedirectUri || window.location.origin
    };

    this.clearAuthState();
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
    }

    this.msalInstance.logoutRedirect(logoutRequest).catch((error) => {
      console.error('[Azure AD] Logout failed:', error);
    });
  }

  isAzureADAuthenticated(): boolean {
    if (!this.msalInstance) return false;
    const accounts = this.msalInstance.getAllAccounts();
    return accounts.length > 0;
  }
}
